<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  stepLabels: string[]
  currentStep: number
  // CSS-only variant — 'participant' (default) uses emerald, 'volunteer' uses sky.
  variant?: 'participant' | 'volunteer'
}>()

const { t } = useI18n()

const isVolunteer = computed(() => props.variant === 'volunteer')
</script>

<template>
  <div class="w-full">
    <p class="text-xs text-slate-500 mb-3 text-center">
      {{ t('registration.step_indicator', { current: currentStep + 1, total: stepLabels.length }) }}
    </p>

    <div class="flex items-start gap-0">
      <div
        v-for="(label, index) in stepLabels"
        :key="index"
        class="flex-1 flex flex-col items-center gap-1.5"
      >
        <!-- Connector + dot row -->
        <div class="flex items-center w-full">
          <!-- Left line -->
          <div
            class="flex-1 h-0.5"
            :class="[
              index === 0 ? 'invisible' : (index <= currentStep
                ? (isVolunteer ? 'bg-sky-500' : 'bg-emerald-500')
                : 'bg-slate-200')
            ]"
          />
          <!-- Dot -->
          <div
            class="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 border-2 transition-colors"
            :class="isVolunteer ? {
              'bg-sky-500 border-sky-500 text-white': index < currentStep,
              'bg-white border-sky-500 text-sky-600': index === currentStep,
              'bg-white border-slate-300 text-slate-400': index > currentStep,
            } : {
              'bg-emerald-500 border-emerald-500 text-white': index < currentStep,
              'bg-white border-emerald-500 text-emerald-600': index === currentStep,
              'bg-white border-slate-300 text-slate-400': index > currentStep,
            }"
          >
            <svg
              v-if="index < currentStep"
              class="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
            </svg>
            <span v-else>{{ index + 1 }}</span>
          </div>
          <!-- Right line -->
          <div
            class="flex-1 h-0.5"
            :class="[
              index === stepLabels.length - 1 ? 'invisible' : (index < currentStep
                ? (isVolunteer ? 'bg-sky-500' : 'bg-emerald-500')
                : 'bg-slate-200')
            ]"
          />
        </div>

        <!-- Label -->
        <span
          class="text-xs text-center max-w-full px-1 leading-tight"
          :class="isVolunteer ? {
            'text-sky-600 font-semibold': index === currentStep,
            'text-sky-500': index < currentStep,
            'text-slate-400': index > currentStep,
          } : {
            'text-emerald-600 font-semibold': index === currentStep,
            'text-emerald-500': index < currentStep,
            'text-slate-400': index > currentStep,
          }"
        >
          {{ label }}
        </span>
      </div>
    </div>
  </div>
</template>
